'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { AuthQueryOptions } from '@/api';
import { hasFullMarketingConsent } from '@/hooks/useAcceptMarketingConsent';
import { useAuthStore } from '@/stores/AuthStore';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { ATTENDANCE_COMPLETE_COPY, AttendanceCompleteSheet } from '../_components/AttendanceCompleteSheet';
import { resolveAutoCheckInEvaluation, shouldFireAutoCheckIn } from '../_utils/resolveAutoCheckInEligibility';
import { useCheckInAttendance } from '../attendance/_hooks/useAttendance';

/**
 * 혜택홈 진입 시 자동 출석체크. 게이트(로그인·성인·마케팅 동의) 미충족이면 유도 없이
 * 침묵하고 수동 경로(출석 페이지)만 남긴다. 판정은 캐시가 아니라 진입 시점의
 * 신선한 GET(staleTime 0) 결과로만 한다 — 프리페치·5분 fresh 캐시로 판정하면
 * 다른 탭·출석 페이지에서 이미 체크인한 상태를 오판해 중복 POST가 된다.
 * 성공 알림은 출석 완료 시트(ResponsiveDialog)가 담당한다.
 */
export const useAutoAttendanceCheckIn = t0 => {
  const $ = _c(13);
  const {
    enabled
  } = t0;
  const queryClient = useQueryClient();
  const isLogin = useAuthStore(_temp);
  const myInfo = useAuthStore(_temp2);
  const hasEvaluatedRef = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      silent: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    mutate: checkIn
  } = useCheckInAttendance(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = AuthQueryOptions["get:/wello/v2/auth/benefit-attendance"]();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = enabled && isLogin;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      ...t2,
      enabled: t3,
      select: _temp3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    data: t5
  } = useQuery(t4);
  const isCheckedInToday = t5 === undefined ? false : t5;
  let t6;
  let t7;
  if ($[4] !== checkIn || $[5] !== enabled || $[6] !== isLogin || $[7] !== myInfo || $[8] !== queryClient) {
    t6 = () => {
      if (!enabled || hasEvaluatedRef.current) {
        return;
      }
      if (!isLogin || !myInfo) {
        return;
      }
      const evaluation = resolveAutoCheckInEvaluation({
        age: myInfo.age,
        isMarketingConsented: hasFullMarketingConsent(myInfo.notificationOptions)
      });
      if (evaluation === "wait-for-consent") {
        return;
      }
      hasEvaluatedRef.current = true;
      if (evaluation === "stop") {
        return;
      }
      queryClient.query({
        ...AuthQueryOptions["get:/wello/v2/auth/benefit-attendance"](),
        staleTime: 0
      }).then(response_0 => {
        const attendance = {
          alreadyCheckedToday: response_0.context?.alreadyCheckedTodayYN ?? false,
          buttonState: response_0.context?.buttonState ?? "DONE"
        } as const;
        if (!shouldFireAutoCheckIn(attendance)) {
          return;
        }
        checkIn({}, {
          onSuccess: _temp4
        });
      }).catch(_temp5);
    };
    t7 = [enabled, isLogin, myInfo, queryClient, checkIn];
    $[4] = checkIn;
    $[5] = enabled;
    $[6] = isLogin;
    $[7] = myInfo;
    $[8] = queryClient;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== isCheckedInToday) {
    t8 = {
      isCheckedInToday
    };
    $[11] = isCheckedInToday;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
function _temp(state) {
  return state.isLogin === true;
}
function _temp2(state_0) {
  return state_0.myInfo;
}
function _temp3(response) {
  return (response.context?.alreadyCheckedTodayYN ?? false) || response.context?.buttonState === "DONE";
}
function _temp4() {
  return openResponsiveDialog({
    component: AttendanceCompleteSheet,
    ariaLabel: ATTENDANCE_COMPLETE_COPY.title,
    drawerType: "bottom",
    modalHeader: ATTENDANCE_COMPLETE_COPY
  });
}
function _temp5() {}